import React, { useEffect, useRef, useState } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Loader, CustomDelete } from 'components';
import { FindLoad, noteStatusEnums, useModal } from 'utils';
import { formActions, goalActions, noteActions, progressReportsActions } from 'store';
import { FORM_SETTINGS, GET_PROGRESS_REPORT, SINGLE_FORM, formModelEnums } from './constants';
import { Properties, FormSections, Attributes, FormBuilderProvider, useFormBuilder } from './formDetail';
import { findUnnamedSectionIndex } from './formDetail/section';
import { ProgressReportBuilderHeader } from './fragments';
import { ReportBuilder } from './progressReport/reportBuilder';
import { ZoomControls, useReportZoom } from './progressReport/zoomControls';
import { seedGoalAttributes } from './utils/goalAttributes';

const CreateProgressReportContent = () => {
    const { setFormList, formList, isDirty, setIsDirty, initialFormListStr, setImageUrls, setActiveSection } = useFormBuilder();
    const form = useSelector((state) => state.forms.form);
    const client = useSelector((state) => state.client.clientItemInfo);
    const progressReportById = useSelector((state) => state.progressReports.progressReportById);
    const params = useParams();
    const dispatch = useDispatch();
    const location = useLocation();
    const formRef = useRef();
    const loader = FindLoad(FORM_SETTINGS);
    const loadForm = FindLoad(SINGLE_FORM);
    const loadReport = FindLoad(GET_PROGRESS_REPORT);
    const info = location?.state;
    const { id, clientId, reportId } = useParams();
    const { open, close } = useModal();
    const navigate = useNavigate();
    // True while phase-1 uploads (pending signatures/images to the file service)
    // run inside handleCreate, before the create/edit request is dispatched.
    // Combined with the ACTION_TYPE loader so the Save button spins across BOTH phases.
    const [isUploading, setIsUploading] = useState(false);
    const reportZoom = useReportZoom();
    // Status the next save sends (DRAFT / COMPLETED); `ReportBuilder` copies it onto the data.
    const statusRef = useRef(null);
    // A new report or a draft can still be saved as a draft; a finalized one cannot go back.
    const savedStatus = useSelector((state) => state.progressReports.progressReportStatuses?.[reportId]);
    const canSaveDraft = !reportId || (savedStatus || progressReportById?.status) === noteStatusEnums.DRAFT;

    useEffect(() => {
        dispatch(formActions.getFormSettings());

        if (id) {
            dispatch(formActions.getFormById(id));
        }

        if (reportId) {
            dispatch(progressReportsActions.getProgressReportById(reportId));
            dispatch(noteActions.viewNote({ ids: [reportId] }));
        }

        // else {
        //     dispatch(formActions.resetFormById());
        // }
    }, []);

    useEffect(() => {
        if (!id) return;
        dispatch(
            formActions.getFormDefault(id, {
                model: formModelEnums.PROGRESSREPORT,
                resourceId: clientId,
                startDate: info?.startDate,
                endDate: info?.endDate,
            })
        );
    }, [id, clientId, info?.startDate, info?.endDate]);

    useEffect(() => {
        if (reportId && progressReportById?.template) {
            // Split imageUrls into context (per-request signed URLs)
            // vs formList (persistable template state).
            const { imageUrls, ...tpl } = progressReportById.template || {};
            setImageUrls?.(imageUrls || {});
            // Goals own their fields from here on — seeded before the dirty baseline.
            const templateParams = seedGoalAttributes(cloneDeep(tpl));
            templateParams.models = formModelEnums.PROGRESSREPORT;
            templateParams.model = formModelEnums.PROGRESSREPORT;
            setFormList(templateParams);
            initialFormListStr.current = JSON.stringify(templateParams);
            setIsDirty(false);
        } else {
            if (form?.id && params?.id) {
                const { imageUrls, ...f } = form || {};
                setImageUrls?.(imageUrls || {});
                const formDataParams = seedGoalAttributes(cloneDeep(f));
                formDataParams.models = f?.models?.[0];
                formDataParams.model = f?.models?.[0];
                setFormList(formDataParams);
                initialFormListStr.current = JSON.stringify(formDataParams);
                setIsDirty(false);
            }
        }
    }, [form, params?.id, reportId, progressReportById]);

    useEffect(() => {
        if (!params?.id && !reportId && formList && !initialFormListStr.current) {
            initialFormListStr.current = JSON.stringify(formList);
        }
    }, [formList, params?.id, reportId]);

    useEffect(() => {
        if (initialFormListStr.current && formList) {
            setIsDirty(JSON.stringify(formList) !== initialFormListStr.current);
        }
    }, [formList]);

    // Removed trapPushed and prevDirty

    useEffect(() => {
        const handleBeforeUnload = (event) => {
            if (isDirty) {
                event.preventDefault();
                event.returnValue = 'You have unsaved changes. Are you sure you want to leave without saving?';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [isDirty]);

    useEffect(() => {
        let isTrapActive = false;

        const handlePopState = (event) => {
            if (isDirty) {
                open(
                    <CustomDelete
                        info="Unsaved Changes"
                        text="You have unsaved changes. Are you sure you want to leave this page?"
                        innerText="Discard Changes"
                        color="blue"
                        handleDel={() => {
                            close();
                            setIsDirty(false);
                            setTimeout(() => {
                                window.history.back();
                            }, 50);
                        }}
                        handleClose={() => {
                            close();
                            window.history.pushState(null, '', window.location.href);
                        }}
                        noClose={true}
                    />
                );
            }
        };

        if (isDirty) {
            window.history.pushState(null, '', window.location.href);
            isTrapActive = true;
            window.addEventListener('popstate', handlePopState);
        }

        return () => {
            if (isTrapActive) {
                window.removeEventListener('popstate', handlePopState);
                window.history.back(); // Automatically pop the trap when tearing down (e.g., normal navigation)
            }
        };
    }, [isDirty]);

    useEffect(() => {
        const handleGlobalClick = (e) => {
            if (isDirty) {
                const link = e.target.closest('a');
                if (link && link.getAttribute('href') && !link.getAttribute('href').startsWith('http') && !link.getAttribute('target')) {
                    const href = link.getAttribute('href');

                    e.preventDefault();
                    e.stopPropagation();

                    open(
                        <CustomDelete
                            info="Unsaved Changes"
                            text="You have unsaved changes. Are you sure you want to leave this page?"
                            innerText="Discard Changes"
                            color="blue"
                            handleDel={() => {
                                close();
                                setIsDirty(false);
                                setTimeout(() => navigate(href), 50); // Use react-router navigate
                            }}
                            handleClose={() => close()}
                            noClose={true}
                        />
                    );
                }
            }
        };

        document.addEventListener('click', handleGlobalClick, true);

        return () => {
            document.removeEventListener('click', handleGlobalClick, true);
        };
    }, [isDirty]);


    useEffect(() => {
        const allSelectedGoals = (progressReportById?.template?.sections || []).flatMap((s) => s?.selectedGoals || []);

        if (reportId && progressReportById && allSelectedGoals?.length) {
            dispatch(
                goalActions.getGoalClinicalData({
                    startDate: progressReportById?.startDate,
                    endDate: progressReportById?.endDate,
                    goalIds: allSelectedGoals?.map((g) => g.id),
                    // The report's own UTC offset, so the graphs date sessions like its PDF.
                    utcOffset: progressReportById?.utcOffset,
                })
            );

            dispatch(
                goalActions.getGoalTargetClinicalData(
                    {
                        startDate: progressReportById?.startDate,
                        endDate: progressReportById?.endDate,
                        goalIds: allSelectedGoals?.map((g) => g.id),
                        utcOffset: progressReportById?.utcOffset,
                    },
                    clientId
                )
            );

            dispatch(
                goalActions.getGoalClinicalDefaultValues({
                    ids: allSelectedGoals?.map((g) => g.id),
                })
            );
        }
    }, [progressReportById]);

    const handleSaveAction = (data) => {
        // Open the unnamed section's settings, where its required name is flagged.
        const unnamedIndex = findUnnamedSectionIndex(formList?.sections);
        if (unnamedIndex !== -1) {
            setActiveSection(unnamedIndex + 1);
            return;
        }
        const sendParams = {
            ...data,
            reportTitle: info?.reportTitle,
            // startDate: info?.startDate,
            // endDate: info?.endDate,
        };
        sendParams.startDate = info?.startDate;
        sendParams.endDate = info?.endDate;

        delete sendParams.createdAt;
        delete sendParams.createdBy;
        delete sendParams.default;
        delete sendParams.model;
        delete sendParams.models;
        delete sendParams.id;
        // The copied template carries its own PUBLISHED status; a report sends only DRAFT / COMPLETED.
        delete sendParams.status;
        if (data.status === noteStatusEnums.DRAFT || data.status === noteStatusEnums.COMPLETED) sendParams.status = data.status;
        sendParams.formId = id;

        if (reportId) {
            dispatch(progressReportsActions.editProgressReport(reportId, sendParams));
        } else {
            dispatch(progressReportsActions.createProgressReport(clientId, sendParams));
        }
    };

    /** Draft: no required-field check, finish later. Submit / Save: validated. */
    const handleHeaderSave = (asDraft) => {
        if (!formRef.current) return;
        statusRef.current = asDraft ? noteStatusEnums.DRAFT : noteStatusEnums.COMPLETED;
        if (asDraft) formRef.current.submitDraft();
        else formRef.current.submit();
    };

    if (loader?.length || loadForm?.length || loadReport?.length) {
        return <Loader />;
    }

    return (
        <div>
            <ProgressReportBuilderHeader onSave={handleHeaderSave} uploading={isUploading} canSaveDraft={canSaveDraft} />
            <>
                <div className="flex-align-start">
                    <FormSections fromReport={true} />
                    <div className="flex-column full-width" style={{ minWidth: 0 }}>
                        <div className="progress-form-body-zoom-area">
                            <div className="progress-form-body-wrapper">
                                {/* CSS `zoom` (not transform) so the scroll area resizes with the report. */}
                                <div style={{ zoom: reportZoom.zoom }}>
                                    <ReportBuilder
                                        resourceId={clientId}
                                        ref={formRef}
                                        handleSaveNote={handleSaveAction}
                                        statusRef={statusRef}
                                        onSigUploadingChange={setIsUploading}
                                        fromReport={true}
                                        // The drag library tracks the pointer in unzoomed pixels, so reorder only at 100%.
                                        allowReorder={reportZoom.zoom === 1}
                                    />
                                </div>
                            </div>
                            <ZoomControls {...reportZoom} />
                        </div>
                    </div>
                    <Properties fromReport={true} />
                </div>

                <Attributes fromBuilder={true} />
            </>
        </div>
    );
};

export const ProgressReportBuilder = () => {
    return (
        <FormBuilderProvider isReportBuilder={true}>
            <CreateProgressReportContent />
        </FormBuilderProvider>
    );
};
